import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, BookmarkCheck, NotebookPen } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { fetchMyTerms, listCategories, formatApiError } from "@/lib/inmindApi";
import CategoryBadge, { categoryLabel } from "@/components/CategoryBadge";

const STATUSES = [
  { value: "history", label: "All you've seen" },
  { value: "new", label: "To review" },
  { value: "reviewed", label: "Reviewed" },
];

export default function HistoryPage() {
  const [terms, setTerms] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("history");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([
        cats.length ? Promise.resolve(cats) : listCategories(),
        fetchMyTerms({ q: q || undefined, category: category !== "all" ? category : undefined, status }),
      ]);
      if (!cats.length) setCats(c);
      setTerms(t);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load library");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q, category, status]);

  return (
    <div className="space-y-5" data-testid="library-page">
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
          Library
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl mt-1 text-[var(--inmind-text)] tracking-tight">
          Everything you've held in mind
        </h2>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--inmind-muted)]" strokeWidth={1.7} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search terms…"
            data-testid="library-search"
            className="pl-9 h-11 rounded-xl"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="library-category"
          className="h-11 rounded-xl border border-black/10 bg-white px-3 text-sm"
        >
          <option value="all">All subjects</option>
          {cats.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="library-status">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatus(s.value)}
            data-testid={`status-${s.value}`}
            className={`text-xs uppercase tracking-wide font-semibold rounded-full px-3 py-1.5 shrink-0 transition-colors ${
              status === s.value
                ? "bg-[var(--inmind-primary)] text-white"
                : "bg-white text-[var(--inmind-text-secondary)] border border-black/10 hover:bg-black/5"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[var(--inmind-muted)]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : terms.length === 0 ? (
        <Card className="rounded-2xl border border-black/5 bg-white">
          <CardContent className="p-8 text-center">
            <p className="font-serif text-xl text-[var(--inmind-text)]">Nothing here yet</p>
            <p className="text-sm text-[var(--inmind-text-secondary)] mt-1">
              Draw today's card to start filling your library.
            </p>
          </CardContent>
        </Card>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="library-list">
          {terms.map((t) => (
            <li key={t.id}>
              <Link
                to={`/term/${t.id}`}
                data-testid={`term-${t.id}`}
                className="block bg-white rounded-2xl border border-black/5 p-4 shadow-[0_4px_18px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-shadow"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <CategoryBadge category={t.category} />
                  {t.status === "reviewed" && (
                    <BookmarkCheck className="w-3.5 h-3.5 text-[var(--inmind-primary)]" strokeWidth={2} />
                  )}
                </div>
                <h3 className="font-serif text-lg text-[var(--inmind-text)] leading-snug">{t.term}</h3>
                <p className="text-sm text-[var(--inmind-text-secondary)] mt-1 line-clamp-2">{t.meaning}</p>
                {t.note_count > 0 && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--inmind-muted)]">
                    <NotebookPen className="w-3 h-3" strokeWidth={1.7} /> {t.note_count} note{t.note_count !== 1 ? "s" : ""}
                  </div>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
